import { all, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { gatewaysActions } from './slice';
// Checks persist onto the device's custom fields, so device fields are refreshed too.
import { devicesActions } from 'reduxStore/devices/slice';
import { gatewayApi, gatewayApiFor } from 'config/gatewayApi';
import { Notification } from 'zComponents/atoms';

// Server error → Error. Reads `error` before `message`: `message` is the generic HTTP text.
const apiError = (res, fallback) => {
	const detail =
		res?.error?.message ||
		(typeof res?.error === 'string' && res.error) ||
		(Array.isArray(res?.error) && res.error.join(', ')) ||
		res?.errors ||
		res?.info ||
		res?.message;
	return new Error(detail || fallback);
};

// Org-wide monitor (GET /gateways/monitor). A vendor outage arrives as 200 with `controlReachable:false`.
export function* loadMonitor() {
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.get(gatewayApi.monitor);
		if (res.statusCode !== 200) throw apiError(res, 'Failed to load the gateway monitor.');
		yield put(gatewaysActions.loadMonitorSuccess(res.data || {}));
	} catch (error) {
		Notification('error', 'Gateway monitor unavailable', error.message || 'Try again shortly.');
		yield put(gatewaysActions.loadMonitorError(error.message));
	}
}

// Status check for one gateway (POST /gateways/:deviceID/check). Throttled and
// vendor-unreachable are answers, not failures.
export function* checkStatus(action) {
	const { deviceID } = action.payload;
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.post(gatewayApiFor(deviceID).check);
		if (res.statusCode !== 200) throw apiError(res, 'Connectivity check failed.');

		if (res.data?.controlReachable === false) {
			Notification('warning', 'Provider unreachable', 'Status unchanged. Try again shortly.');
		} else if (res.data?.throttled) {
			Notification('info', 'Checked recently', 'Showing the result from the last minute.');
		} else {
			// Toast the verdict: bound locks are only re-read server-side when the gateway is online.
			const gatewayName = res.data?.gateway?.name || 'Gateway';
			const status = res.data?.gateway?.status;
			if (status === 'online') {
				Notification('success', `${gatewayName} is online`, 'Status and bound locks are up to date.');
			} else if (status === 'offline') {
				Notification('warning', `${gatewayName} is offline`, 'The provider reports it offline.');
			} else {
				Notification('warning', `${gatewayName} not reported`, 'The provider did not return this gateway.');
			}
			// The sweep wrote history and device fields; refresh every read that renders them.
			yield put(gatewaysActions.applyGatewaySnapshot({ gateway: res.data?.gateway }));
			const history = yield select((state) => state.Gateways.historyByDeviceID?.[deviceID]);
			yield put(gatewaysActions.loadHistory({ deviceID, limit: history?.limit }));
			yield put(gatewaysActions.loadGatewayLocks({ deviceID }));
			yield put(devicesActions.loadDeviceFields({ deviceID }));
		}
		yield put(gatewaysActions.checkStatusSuccess({ deviceID, ...res.data }));
	} catch (error) {
		Notification('error', 'Gateway check failed', error.message || 'Try again shortly.');
		yield put(gatewaysActions.checkStatusError({ deviceID }));
	}
}

// Firmware check (POST /gateways/:deviceID/firmware-check). A 400 (e.g. TTLock) carries the server's reason.
export function* checkFirmware(action) {
	const { deviceID } = action.payload;
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.post(gatewayApiFor(deviceID).firmwareCheck);
		if (res.statusCode !== 200) throw apiError(res, 'Firmware check failed.');

		const firmware = res.data?.firmware;
		Notification(
			'success',
			firmware?.needUpgrade ? 'Firmware update available' : 'Firmware up to date',
			firmware?.needUpgrade ? 'A newer version is ready to install.' : 'This gateway runs the latest version.'
		);
		yield put(gatewaysActions.checkFirmwareSuccess({ deviceID, firmware }));
		// Re-read the persisted verdict so it survives a reload.
		yield put(devicesActions.loadDeviceFields({ deviceID }));
	} catch (error) {
		Notification('error', 'Firmware check failed', error.message || 'Try again shortly.');
		yield put(gatewaysActions.checkFirmwareError({ deviceID }));
	}
}

// One gateway's history: day buckets + paged raw records (`before` appends older). Quiet on failure.
export function* loadHistory(action) {
	const { deviceID, days = 5, limit = 20, before = null } = action.payload;
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const query = before ? { days, limit, before } : { days, limit };
		const res = yield superFetch.get(gatewayApiFor(deviceID).history, {}, query);
		if (res.statusCode !== 200) throw apiError(res, 'Could not load connectivity history.');
		yield put(
			gatewaysActions.loadHistorySuccess({
				deviceID,
				buckets: res.data?.buckets || [],
				records: res.data?.records || [],
				nextBefore: res.data?.nextBefore ?? null,
				append: !!before
			})
		);
	} catch (error) {
		yield put(gatewaysActions.loadHistoryError({ deviceID, error: error.message }));
	}
}

// Day buckets for every gateway in one read (GET /gateways/history). Quiet on failure.
export function* loadOrgHistory(action) {
	const { days = 5 } = action?.payload || {};
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.get(gatewayApi.history, {}, { days });
		if (res.statusCode !== 200) throw apiError(res, 'Could not load connectivity history.');
		yield put(gatewaysActions.loadOrgHistorySuccess({ buckets: res.data?.buckets || {} }));
	} catch (error) {
		yield put(gatewaysActions.loadOrgHistoryError());
	}
}

// Last-known bound locks (heartbeat history, no radio). Quiet on failure.
export function* loadGatewayLocks(action) {
	const { deviceID } = action.payload;
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.get(gatewayApiFor(deviceID).locks);
		if (res.statusCode !== 200) throw apiError(res, 'Could not load bound locks.');
		yield put(
			gatewaysActions.loadGatewayLocksSuccess({
				deviceID,
				checkedAt: res.data?.checkedAt ?? null,
				locks: res.data?.locks || []
			})
		);
	} catch (error) {
		yield put(gatewaysActions.loadGatewayLocksError({ deviceID }));
	}
}

// Import one unmanaged vendor gateway, then assign its site (a failed site is a warning).
export function* syncGateway(action) {
	const { gatewayId, name, siteID, connectionOverride } = action.payload;
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.post(gatewayApi.sync, { gatewayId, name, connectionOverride });
		const deviceID = res.data?.deviceID;
		if (res.statusCode !== 200 || !deviceID) throw apiError(res, 'Failed to add the gateway to inventory.');

		if (siteID) {
			const siteRes = yield superFetch.put(`/devices/${deviceID}/sites`, { siteIDs: [siteID] });
			if (siteRes.statusCode !== 200) {
				Notification('warning', 'Gateway added', 'Site not assigned. Set it in the gateway drawer.');
			}
		}
		Notification('success', 'Gateway added', 'It is now in your inventory.');
		yield put(gatewaysActions.syncGatewaySuccess({ gatewayId }));
		yield put(gatewaysActions.loadMonitor());
	} catch (error) {
		Notification('error', 'Gateway not added', error.message || 'Try again shortly.');
		yield put(gatewaysActions.syncGatewayError({ gatewayId }));
	}
}

// Org setting 161: scheduled connectivity monitoring.
const SETTING = { ENABLED: 161 };

/** Map a raw org-settings list (GET /settings shape) to the monitoring config. */
const toConnectivityConfig = (settings) => {
	const byId = {};
	(settings || []).forEach((s) => {
		byId[s.defaultSettingID] = s.value;
	});
	return { enabled: byId[SETTING.ENABLED] === '1' };
};

// Read setting 161 from the Settings store first; fetch only before boot or with `force`.
export function* loadConnectivityConfig(action) {
	if (!action?.payload?.force) {
		const stored = yield select((state) =>
			state.Settings.orgSettingsLoadingComplete
				? state.Settings.orgSettings[state.Auth.selectedProfile?.orgID]
				: null
		);
		if (stored) {
			yield put(gatewaysActions.loadConnectivityConfigSuccess(toConnectivityConfig(stored)));
			return;
		}
	}
	const superFetch = (yield import('library/helpers/superFetch')).default;
	try {
		const res = yield superFetch.get('/settings');
		if (res.statusCode !== 200) throw apiError(res, 'Failed to load settings.');
		yield put(gatewaysActions.loadConnectivityConfigSuccess(toConnectivityConfig(res.data)));
	} catch (error) {
		yield put(gatewaysActions.loadConnectivityConfigError());
	}
}

export default function* gatewaysSaga() {
	yield all([
		takeLatest(gatewaysActions.loadMonitor, loadMonitor),
		// takeEvery: checks on different gateways must not cancel each other.
		takeEvery(gatewaysActions.checkStatus, checkStatus),
		takeEvery(gatewaysActions.checkFirmware, checkFirmware),
		takeEvery(gatewaysActions.loadHistory, loadHistory),
		// takeLatest: it answers for the whole org, so a second in-flight read supersedes the first.
		takeLatest(gatewaysActions.loadOrgHistory, loadOrgHistory),
		// takeLatest: drawer open and the row's Bound locks ask at once; one read is enough.
		takeLatest(gatewaysActions.loadGatewayLocks, loadGatewayLocks),
		takeEvery(gatewaysActions.syncGateway, syncGateway),
		takeLatest(gatewaysActions.loadConnectivityConfig, loadConnectivityConfig)
	]);
}
